import DataGrid, { Column, Pager, Paging, Selection } from 'devextreme-react/data-grid';
import * as Store from '@apply/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faDownload, faPencil } from '@fortawesome/free-solid-svg-icons';
import { confirmModal } from '@apply/ui';
import { formatDate, mapType } from './../NoticesBase.tsx';

export interface NoticesProps {
    notices: Store.UserStore.Types.UserNoticeLean[] | undefined;
    onClickViewNotice: (noticeId: number) => void;
    markAsRead: (notice: Store.UserStore.Types.UserNoticeLean) => void;
    deleteNotice: (noticeId: number) => void;
    loadingOpen: boolean;
    defaultPageSize: number;
    allowedPageSizes: Array<number>;
}

export const Notices: React.FC<NoticesProps> = ({
    notices,
    onClickViewNotice,
    markAsRead,
    deleteNotice,
    loadingOpen,
    defaultPageSize,
    allowedPageSizes,
}) => {
    const onRowPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }

        if (e.data.eSignTypeId === Store.UserStore.Types.ESignType.Required && !e.data.eSignDate?._isValid) {
            const classNames = e.rowElement.className.replace('dx-row-alt', '');
            e.rowElement.className = classNames + ' dx_portfolio_highlight1';
        }
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const onTypeRender = (e) => {
        return mapType(e.value);
    };

    const onDelete = async (noticeId: number) => {
        confirmModal({
            message: 'Are you sure you want to delete this notification? The notification will no longer be visible in the grid.',
            title: 'Delete Confirmation',
            onConfirm: () => {
                deleteNotice(noticeId);
            },
        });
    };

    const onNoticeFilesRender = (e) => {
        const f = e.data?.signedFile ?? e.data?.noticeFile;
        if (!f) {
            return 'Not Avail.';
        } else {
            return (
                <a
                    href={f.url}
                    target='_blank'
                    title={f.address ?? f.name}
                    onClick={() => markAsRead(e.data)}
                >
                    <FontAwesomeIcon
                        icon={faDownload}
                        aria-hidden='true'
                    />
                </a>
            );
        }
    };

    const onDeleteRender = (e) => {
        return (
            <button
                className={`notice-action-button ${!e.data.canDelete ? 'disabled' : ''}`}
                onClick={() => onDelete(e.data.id)}
                title='Delete Notice'
                disabled={!e.data.canDelete}
            >
                <FontAwesomeIcon
                    icon={faTrashCan}
                    aria-hidden='true'
                />
            </button>
        );
    };

    const onAddressCellRender = (e) => {
        if (!e.value) {
            return null;
        }
        return e.value.map((a, index) => {
            return (
                <div
                    key={'addr' + index}
                    style={{ marginBottom: '5px' }}
                >
                    {a.street1} {a.street2}
                    <br />
                    {a.city}, {a.state} {a.zipCode}
                </div>
            );
        });
    };

    const onSignRender = (e) => {
        return (
            <div>
                {e.data.eSignTypeId !== Store.UserStore.Types.ESignType.NotRequired && (
                    <button
                        className='notice-action-button'
                        // eslint-disable-next-line
                        onClick={(event) => onClickViewNotice(e.data.id)}
                        title={
                            e.data.isManual
                                ? 'View Unavailable. Download only.'
                                : e.data.eSignDate?._isValid
                                  ? 'View Notice'
                                  : 'Sign Notice'
                        }
                        disabled={e.data.isManual}
                    >
                        {e.data.eSignDate?._isValid ? (
                            <FontAwesomeIcon
                                icon={faEye}
                                aria-hidden='true'
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faPencil}
                                aria-hidden='true'
                            />
                        )}
                    </button>
                )}
            </div>
        );
    };

    const onCellDblClick = (e) => {
        onClickViewNotice(e.data.id);
    };

    return (
        <div>
            {!loadingOpen && notices?.length > 0 && (
                <>
                    <div className='columns small-12 large-12'>
                        <div
                            style={{
                                backgroundColor: '#f9f5d5',
                                padding: '5px',
                                textAlign: 'center',
                            }}
                        >
                            Highlighted rows require signature
                        </div>
                    </div>
                    <div className='columns small-12 large-12'>
                        <DataGrid
                            keyExpr='id'
                            id='noticeTable'
                            dataSource={notices}
                            showBorders={true}
                            showRowLines={false}
                            rowAlternationEnabled={true}
                            remoteOperations={false}
                            allowColumnResizing={true}
                            allowColumnReordering={false}
                            columnResizingMode={'nextColumn'}
                            columnMinWidth={20}
                            onCellDblClick={onCellDblClick}
                            cellHintEnabled={true}
                            onRowPrepared={onRowPrepared}
                            width={'auto'}
                        >
                            <Selection mode='single' />
                            <Column
                                dataField='noticeTypeId'
                                caption='Type'
                                width={130}
                                cellRender={onTypeRender}
                                alignment='center'
                            />
                            <Column
                                caption='Address'
                                dataField='addresses'
                                alignment='left'
                                minWidth={130}
                                allowSorting={false}
                                cellRender={onAddressCellRender}
                            />
                            <Column
                                dataField='noticeDate'
                                caption='Date/Time'
                                cellRender={onDateRender}
                                width={120}
                            />
                            <Column
                                dataField='isRead'
                                caption='Read'
                                width={50}
                            />
                            <Column
                                caption='Signed'
                                dataField='eSignName'
                                width={130}
                            />
                            <Column
                                caption='File'
                                width={60}
                                cellRender={onNoticeFilesRender}
                                alignment='center'
                            />
                            <Column
                                caption='Sign'
                                cellRender={onSignRender}
                                width={60}
                                alignment='center'
                            />
                            <Column
                                caption='Delete'
                                cellRender={onDeleteRender}
                                width={60}
                                alignment='center'
                            />
                            <Paging defaultPageSize={defaultPageSize} />
                            <Pager
                                showPageSizeSelector={allowedPageSizes.length > 1}
                                allowedPageSizes={allowedPageSizes}
                                showNavigationButtons={true}
                                showInfo={true}
                                visible={true}
                            />
                        </DataGrid>
                    </div>
                </>
            )}
            {!loadingOpen && !notices?.length && (
                <ul>
                    <li>
                        <em>No Notices at this time</em>
                    </li>
                </ul>
            )}
        </div>
    );
};
